import {Trans} from '@lingui/macro';
import {Duration} from 'luxon';
import React from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {formatDuration} from '../../../helpers/dateTimeHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {SettingsFieldLine} from '../../common/settingsFieldLine';

/*
 * Props.
 */

export interface SettingsCommonChannelFieldProps<T extends ChannelModel> {
  onChange: HandlerOf<Partial<ChannelModel>>;
  onPreferencesChange: HandlerOf<Partial<ChannelModel['preferences']>>;
  setDirty: Handler;
  edited: T;
}

/*
 * Components.
 */

const possibleUndoTimings = [
  Duration.fromObject({seconds: 5}),
  Duration.fromObject({seconds: 10}),
  Duration.fromObject({seconds: 15}),
  Duration.fromObject({seconds: 30}),
  Duration.fromObject({seconds: 60}),
];

export const ChannelUndoSendTimingField = <T extends ChannelModel>(
  props: SettingsCommonChannelFieldProps<T>,
) => {
  const value = Duration.fromObject({seconds: props.edited.preferences.undoSendPeriod}).toMillis();

  return (
    <SettingsFieldLine
      title={<Trans id="settingschannel-undosend-title">Undo send timing</Trans>}
      subtitle={
        <Trans id="settingschannel-undosend-subtitle">Front will hold your message before sending it</Trans>
      }
      content={
        <DropdownBoxCoordinator
          label={null}
          value={
            value === 0 ? (
              <Trans id="settingschannel-sendimmediately">Send immediately</Trans>
            ) : (
              formatDuration(Duration.fromMillis(value), {unitDisplay: 'long'})
            )
          }
          possibleValues={[
            <Trans id="settingschannel-sendimmediately">Send immediately</Trans>,
            ...possibleUndoTimings.map((v) => formatDuration(v, {unitDisplay: 'long'})),
          ]}
          renderDropdown={(popoverProps) => (
            <SimpleDropdown {...popoverProps}>
              {[
                {title: <Trans id="settingschannel-sendimmediately">Send immediately</Trans>, value: 0},
                ...possibleUndoTimings.map((durationObject) => ({
                  title: formatDuration(durationObject, {unitDisplay: 'long'}),
                  value: durationObject.toMillis(),
                })),
              ].map((item) => (
                <SimpleDropdownItem
                  key={item.value}
                  isChecked={value === item.value}
                  onSelect={() => {
                    props.onPreferencesChange({
                      undoSendPeriod: item.value,
                    });
                  }}
                >
                  {item.title}
                </SimpleDropdownItem>
              ))}
            </SimpleDropdown>
          )}
        />
      }
    />
  );
};

export const ChannelReplyControlField = <T extends ChannelModel>(
  props: SettingsCommonChannelFieldProps<T>,
) => (
  <SettingsFieldLine
    title={<Trans id="settingschannel-replywithoutinboxaccess-title">All teammates can reply</Trans>}
    subtitle={
      <Trans id="settingschannel-replywithoutinboxaccess-subtitle">
        All teammates can reply to conversations send to this channel without needing access to the inbox Chat
      </Trans>
    }
    isSubtitleMultiline
    content={
      <Toggle
        isChecked={props.edited.preferences.canReplyWithoutInboxAccess}
        onChange={(v) => {
          props.onPreferencesChange({
            canReplyWithoutInboxAccess: v,
          });
        }}
      />
    }
  />
);

export const ChannelAutoAssignField = <T extends ChannelModel>(props: SettingsCommonChannelFieldProps<T>) => (
  <SettingsFieldLine
    title={<Trans id="settingschannel-autoassign-title">Auto-assign conversations on first reply</Trans>}
    subtitle={
      <Trans id="settingschannel-autoassign-subtitle">
        When a teammate replies to an unassigned conversation, assign it to them.
      </Trans>
    }
    isSubtitleMultiline
    content={
      <Toggle
        isChecked={props.edited.preferences.shouldAutoAssign}
        onChange={(v) => {
          props.onPreferencesChange({
            shouldAutoAssign: v,
          });
        }}
      />
    }
  />
);
